import React, { useLayoutEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { PermissionsGroupId, CampaignBody, CampaignResponse, HeroModuleItemType } from '../../../../API';
import type { ContentItem } from './ContentItemPreview';
import TextField from '../../../shared/TextField';
import Button from '../../../shared/Button';
import Drawer from '../../../shared/Drawer';
import { Stack } from '@mui/system';
import { Autocomplete, Typography } from '@mui/material';
import { IdBadge } from '../../../shared/IdBadge';
import { campaignFormTestIds } from '../../../shared/TestsIds';
import { useData } from '../../../../data-layer';
import { useLocales } from '../../../../hooks';
import { useRecoilValue } from 'recoil';
import { makeStyles } from 'tss-react/mui';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { HomepageOptions } from '../../../../state/theme';
import { usePermissions } from '../../../../hooks/Permissions/usePermissions';
import { ValidatorForm } from 'react-material-ui-form-validator';
import DateTimeRange from '../../../shared/DateTimeRange';
import CountryPickerLite from '../../../shared/CountryPickerLite';
import { isFormValid as isFormValidHelper, markAsRequired } from '../../../../utils/formHelpers';
import { Sortable } from '../../../shared/Sortable';
import { ContentItemPreview } from './ContentItemPreview';
import { ContentItemForm, ContentItemFormMetadata } from './ContentItemForm';
import generateId from '../../../../utils/generateId';
import { DateTime } from 'luxon';
import TextValidator from '../../../shared/TextValidator';
import { URL_REGEX } from '../../../../utils/regex';

// Form-specific type that extends the API type but with proper contentIds structure
type CampaignFormData = Omit<CampaignBody, 'contentIds'> & {
  contentIds: ContentItem[];
  trackingUrl?: string;
};

type CampaignResponseFormData = Omit<CampaignResponse, 'contentIds'> & {
  contentIds: ContentItem[];
  trackingUrl?: string;
};

const useStyles = makeStyles()((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4)
  },
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(4)
  },
  footerButtons: {
    display: 'flex',
    gap: theme.spacing(4)
  },
  footerButton: {
    minWidth: 160
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(4)
  },
  autocomplete: {
    display: 'flex'
  },
  container: {
    borderBottom: `1px dotted ${theme.palette.divider}`,
    padding: theme.spacing(3, 0)
  },
  trackingUrl: {
    width: '100%'
  }
}));

export function CampaignForm(): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();

  const {
    campaigns: {
      state: { withFormMetadata, withIsSaving },
      hook: { save: saveCampaign, closeForm: closeCampaignForm }
    },
    sponsors: {
      state: { withAllRecords: allSponsors, withAllRecordsById: sponsorsById }
    }
  } = useData();

  const methods = useForm<CampaignFormData | CampaignResponseFormData>();
  const { control, handleSubmit, reset } = methods;

  const formMetadata = useRecoilValue(withFormMetadata);
  const isSaving = useRecoilValue(withIsSaving);
  const allSponsorsData = useRecoilValue(allSponsors);
  const sponsorsByIdData = useRecoilValue(sponsorsById);
  const { append, remove, fields, replace, update } = useFieldArray({ control, name: 'contentIds' });
  const campaign = formMetadata.record;
  const formRef = useRef<ValidatorForm>(null);

  const [startDate, setStartDate] = useState<string | undefined>(DateTime.now().toISO());
  const [endDate, setEndDate] = useState<string | undefined>(DateTime.now().plus({ day: 1 }).toISO());
  const [contentItemFormMetadata, setContentItemFormMetadata] = useState<ContentItemFormMetadata>();

  const { canSave } = usePermissionsGuard({
    homepageOption: HomepageOptions.MONETIZATION
  });
  const { hasUpsertPermission } = usePermissions();

  useLayoutEffect(() => {
    if (campaign) {
      // Transform the campaign data to match the form structure
      const formData: CampaignFormData | CampaignResponseFormData = {
        ...campaign,
        contentIds: (campaign.contentIds || []).map((contentId: string) => ({
          id: generateId(),
          contentId,
          type: HeroModuleItemType.VOD
        }))
      };
      reset(formData);
    }
  }, [campaign, reset]);

  const onClose = () => {
    closeCampaignForm();
  };

  const isFormValid = async () => {
    return startDate !== '' && endDate !== '' && (await isFormValidHelper(formRef));
  };

  const onSubmit = async (campaign: CampaignFormData | CampaignResponseFormData) => {
    if (!(await isFormValid())) return;

    // Transform contentIds from objects to strings array
    const baseCampaignData = {
      sponsorId: typeof campaign.sponsorId === 'string' ? campaign.sponsorId : campaign.sponsorId[0],
      contentIds: fields.map((item: ContentItem) => item.contentId).filter(Boolean),
      startDate,
      endDate,
      countries: campaign.countries || [],
      trackingUrl: campaign.trackingUrl
    };

    // Determine if this is an edit or create operation
    const isEditing = formMetadata.isEditing && 'id' in campaign;

    const campaignToSave = isEditing
      ? ({
          ...baseCampaignData,
          id: (campaign as CampaignResponseFormData).id
        } as CampaignResponse)
      : (baseCampaignData as CampaignBody);
    const savedCampaign = await saveCampaign(campaignToSave);

    if (savedCampaign) {
      onClose();
    }
  };

  const handleAdd = () => {
    setContentItemFormMetadata({
      isShowingForm: true,
      isEditing: false,
      record: {
        id: generateId(),
        contentId: '',
        type: HeroModuleItemType.VOD
      }
    });
  };

  const handleEdit = (index: number, contentItem: ContentItem) => {
    setContentItemFormMetadata({
      isShowingForm: true,
      isEditing: true,
      index,
      record: contentItem
    });
  };

  const onContentItemSubmit = (contentItemMetadata: ContentItemFormMetadata) => {
    if (contentItemMetadata.index !== undefined && contentItemMetadata.index >= 0) {
      update(contentItemMetadata.index as number, contentItemMetadata.record as ContentItem);
    } else {
      append(contentItemMetadata.record as ContentItem);
    }
    setContentItemFormMetadata(undefined);
  };

  const onContentItemDelete = (contentItemMetadata: ContentItemFormMetadata) => {
    if (contentItemMetadata.index !== undefined && contentItemMetadata.index >= 0) {
      remove(contentItemMetadata.index);
    }
    setContentItemFormMetadata(undefined);
  };

  const footerLeft = (
    <div className={classes.footerButtons}>
      <Button
        className={classes.footerButton}
        color="primary"
        loading={isSaving}
        onClick={handleSubmit(onSubmit)}
        disabled={!hasUpsertPermission(PermissionsGroupId.MONETIZATION) && !canSave}
        data-testid={campaignFormTestIds.saveButton}
      >
        {t('general.save')}
      </Button>
    </div>
  );

  const footerRight = (
    <Button className={classes.footerButton} color="grey" onClick={onClose}>
      {t('general.cancel')}
    </Button>
  );

  return (
    <Drawer
      open={formMetadata.isShowingForm}
      onClose={onClose}
      headerLeft={
        <Stack direction="row" gap={4}>
          <Typography variant="h6">{t(`payments.${formMetadata.isEditing ? 'edit' : 'new'}_campaign`)}</Typography>
          {formMetadata.isEditing && campaign && (
            <IdBadge id={(campaign as CampaignResponse).id} data-testid={campaignFormTestIds.campaignId} />
          )}
        </Stack>
      }
      footerLeft={footerLeft}
      footerRight={footerRight}
      formRef={formRef}
    >
      {campaign && (
        <div className={classes.formBody} data-testid={campaignFormTestIds.formBody}>
          <FormProvider {...methods}>
            <div className={classes.formRow}>
              <Controller
                control={control}
                name="sponsorId"
                rules={{ required: t('general.field_is_required') }}
                render={({ field: { value, onChange }, fieldState: { error } }) => {
                  const sponsor = sponsorsByIdData[value];
                  return (
                    <Autocomplete
                      className={classes.autocomplete}
                      multiple={false}
                      options={allSponsorsData || []}
                      getOptionLabel={(sponsor) => sponsor.name || sponsor.slogan || ''} // Used for searching
                      filterOptions={(options, params) => {
                        const searchTerm = params.inputValue.toLowerCase();
                        return options.filter(
                          (sponsor) =>
                            sponsor.name?.toLowerCase().includes(searchTerm) ||
                            sponsor.slogan?.toLowerCase().includes(searchTerm)
                        );
                      }}
                      disableClearable
                      blurOnSelect
                      fullWidth
                      renderOption={(props, sponsor) => (
                        <li data-testid={campaignFormTestIds.sponsorsOption} {...props}>
                          {sponsor.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={markAsRequired(t('payments.sponsors'), true)}
                          fieldError={error}
                        />
                      )}
                      value={sponsor}
                      onChange={(_, changedValues) => {
                        const newSelectedSponsor = changedValues;
                        onChange(newSelectedSponsor ? [newSelectedSponsor.id] : []);
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      data-testid={campaignFormTestIds.sponsors}
                    />
                  );
                }}
              />
            </div>

            <div className={classes.header}>
              <Typography variant="h6">{t('payments.campaign.contentIds')}</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                {t('layouts.drag_text')}
              </Typography>
            </div>
            <Sortable
              list={fields}
              setList={replace}
              animation={100}
              ghostClass="sortableGhost"
              dragClass="sortableDragDefault"
            >
              {fields.map((contentItem, i) => (
                <div className={classes.container} key={contentItem.id} data-testid={campaignFormTestIds.contentItem}>
                  <ContentItemPreview
                    index={i}
                    contentItem={contentItem as ContentItem}
                    onRemove={remove}
                    onEdit={() => handleEdit(i, contentItem as ContentItem)}
                  />
                </div>
              ))}
            </Sortable>
            <div>
              <Button color="grey" onClick={handleAdd}>
                {t('payments.campaign.add_content_item')}
              </Button>
            </div>

            <DateTimeRange
              startTimeState={[startDate, setStartDate]}
              endTimeState={[endDate, setEndDate]}
              minDateRequired
              maxDateRequired
            />
            <div className={classes.formRow}>
              <Controller
                name="countries"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CountryPickerLite
                    data-testid={campaignFormTestIds.countryPicker}
                    label={t('payments.campaign.countries')}
                    value={value}
                    onChange={onChange}
                    required={true}
                    fullWidth={true}
                  />
                )}
              />
            </div>
            <div className={classes.formRow}>
              <Controller
                control={control}
                name="trackingUrl"
                rules={{
                  pattern: {
                    value: new RegExp(URL_REGEX),
                    message: t('general.field_match_url')
                  }
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <TextValidator
                    name="trackingUrl"
                    label={t('payments.campaign.tracking_url')}
                    className={classes.trackingUrl}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    data-testid={campaignFormTestIds.trackingUrl}
                  />
                )}
              />
            </div>
          </FormProvider>
        </div>
      )}
      <ContentItemForm
        formMetadata={contentItemFormMetadata}
        onSubmit={onContentItemSubmit}
        onDelete={onContentItemDelete}
      />
    </Drawer>
  );
}
